import React, { PropsWithChildren, useMemo, useCallback } from 'react';

// Elements
import ringIconUrl from 'url:../../images/ring_logo_white.png';
import { TopNavigation, TopNavigationProps } from '@amzn/awsui-components-react';
import { useTheme } from '../../style/themeContext';
import { logout } from '../../pages/App';
import { UserPermissions } from '../../common/UserPermissions';
import { UserRole } from '../../common/EmployeeContext';

// Access Control Functions
const createCoachingAccess = (org: string, userPermissions: Set<string>): boolean =>
    userPermissions.has(UserPermissions.CreateCoaching) && (org === 'Ring' || org === 'Amazon');

const viewCoachingAccess = (org: string, userPermissions: Set<string>): boolean =>
    userPermissions.has(UserPermissions.ViewCoaching) && (org === 'Ring' || org === 'Amazon');

type Props = PropsWithChildren<{
    userPermissions: Set<string>;
    menuClick: (menuKey: string) => void;
    currentView: string;
    user: string;
    org: string;
    role?: UserRole;
}>;

function Header(props: Props) {
    const {
        userPermissions,
        menuClick,
        currentView,
        user,
        org,
        role,
    } = props;

    const { toggleThemePreference } = useTheme();

    // Handle menu item clicks
    const handleMenuClick = useCallback((event: { detail: { id: string } }) => {
        const { id } = event.detail;
        switch (id) {
            case 'createCoaching':
                menuClick('Create Coaching');
                break;
            case 'createCsqa':
                menuClick('Create CSQA');
                break;
            case 'coaching':
                menuClick('Coaching Dashboard');
                break;
            case 'csqa':
                menuClick('CSQA Dashboard');
                break;
            default:
                break;
        }
    }, [menuClick, currentView]);

    // Build Create Menu Items based on permissions
    const createItems = useMemo(() => {
        const items = [];

        if (createCoachingAccess(org, userPermissions)) {
            items.push({
                id: 'createCoaching',
                text: 'Create Coaching',
            });
        }

        if (userPermissions.has(UserPermissions.CreateCsqa)) {
            items.push({
                id: 'createCsqa',
                text: 'Create CSQA',
            });
        }

        return items;
    }, [org, userPermissions]);

    // Build Dashboard Menu Items based on permissions
    const dashboardItems = useMemo(() => {
        const items = [];

        if (viewCoachingAccess(org, userPermissions)) {
            items.push({
                id: 'coaching',
                text: 'Coaching Dashboard',
            });
        }

        if (userPermissions.has(UserPermissions.ViewCsqa)) {
            items.push({
                id: 'csqa',
                text: 'CSQA Dashboard',
            });
        }

        return items;
    }, [org, userPermissions]);

    // Informational items (user details)
    const infoItems = useMemo(() => ([
        {
            id: 'user-info',
            text: `User: ${user}`,
            disabled: true,
        },
        {
            id: 'role-info',
            text: `Role: ${role}`,
            disabled: true,
        },
        {
            id: 'org-info',
            text: `Org: ${org}`,
            disabled: true,
        },
    ]), [user, role, org]);

    const optionItems = [
        {
            id: 'toggleTheme',
            text: `Switch Theme`,
        },
        {
            id: 'logout',
            text: `Logout`,
        },
    ];

    // Consolidate navigation items as separate dropdowns and buttons
    const menu: TopNavigationProps.Utility[] = useMemo(() => {
        const viewSelectionItems: TopNavigationProps.MenuDropdownUtility[] = [];

        if (createItems.length > 0) {
            viewSelectionItems.push({
                type: 'menu-dropdown',
                text: 'Create',
                items: createItems,
            });
        }

        if (dashboardItems.length > 0) {
            viewSelectionItems.push({
                type: 'menu-dropdown',
                text: 'Dashboard',
                items: dashboardItems,
            });
        }

        const settingItems: TopNavigationProps.MenuDropdownUtility[] = [
            {
                type: 'menu-dropdown',
                text: 'User Details',
                items: infoItems,
            },
            {
                type: 'menu-dropdown',
                text: 'Settings',
                items: optionItems,
            }
        ];

        return [
            {
                type: 'menu-dropdown',
                'data-testid': 'navigation-menu',
                text: `${currentView}`,
                items: viewSelectionItems,
                onItemClick: handleMenuClick,
            },
            {
                type: 'menu-dropdown',
                'data-testid': 'hamburger-menu',
                iconName: 'menu',
                items: settingItems,
                onItemClick: (event: CustomEvent<{ id: string }>) => {
                    const { id } = event.detail;
                    if (id === 'toggleTheme') {
                        toggleThemePreference();
                    }
                    if (id === 'logout') {
                        logout();
                    }
                },
            },
        ];
    }, [createItems, dashboardItems, handleMenuClick, toggleThemePreference]);

    return (
        <TopNavigation
            identity={{
                href: '',
                title: 'Engagement Suite',
                logo: {
                    src: ringIconUrl,
                    alt: 'EngagementSuite',
                },
            }}
            utilities={menu}
            i18nStrings={{
                overflowMenuTriggerText: 'More',
                overflowMenuTitleText: 'All',
                overflowMenuBackIconAriaLabel: 'Back',
                overflowMenuDismissIconAriaLabel: 'Close menu',
            }}
        />
    );
}

export default Header;
